import cPLogoDark from "@assets/svg/cp-logo-dark.svg"
import cPLogo from "@assets/svg/cp-logo.svg"
import ChatIcon from "@assets/svg/navigation/chat-bubble-alt.svg?react"
import DashboardIcon from "@assets/svg/navigation/dashboard.svg?react"
import LogoutIcon from "@assets/svg/navigation/logout.svg?react"
import SearchIcon from "@assets/svg/search-icon.svg?react"
import { AEMImage } from "@components/AEMImage"
import { AccountMenu } from "@components/drawers/AccountMenu/AccountMenu"
import { Notifications } from "@components/drawers/Notifications/Notifications"
import { YourExtensions } from "@components/drawers/YourExtensions/YourExtensions"
import { LanguagePicker, ThemeSwitch } from "@components/ui"
import DrawerWithTrigger from "@components/ui/DrawerWithTrigger/DrawerWithTrigger"
import { NotificationBell } from "@components/ui/NotificationBell/NotificationBell"
import { getWeatherIcon } from "@components/ui/WeatherWidget/WeatherWidget"
import { useDismissedNotifications } from "@hooks/useDismissedNotifications"
import { getLocation } from "@hooks/useLocation"
import {
	AppShell,
	Avatar,
	Burger,
	Divider,
	Flex,
	Group,
	Image,
	MantineStyleProps,
	Text,
	UnstyledButton,
	useMantineColorScheme
} from "@mantine/core"
import { getWeather } from "@services/weatherService"
import {
	VITE_USE_DARK_MODE,
	VITE_USE_LANGUAGE_PICKER,
	USE_GLOBAL_SEARCH,
	SHOW_MULTI_AGENCY
} from "@utils/constants.util"
import { useIsMobile, useIsTablet } from "@utils/device.utils"
import { eventTrackingItems } from "@utils/eventTracking"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useAppShellContext } from "@context/AppShellProvider"
import { useAuthContext } from "@context/AuthProvider"
import { useUserStore } from "@hooks/useUserStore"
import styles from "./AppHeader.module.css"
import { useAEMNotificationsData } from "@AEM/hooks/useAEMNotificationsData"
import { AEMNotificationItem } from "@AEM/models/AEMResponse.model"
import { useAEMDictionary } from "@AEM/hooks/useAEMDictionary"
import {
	AEM_APP_SETTINGS_COLLECTIONS,
	useAEMAppSettings
} from "@AEM/hooks/useAEMSettings"

function AppHeader({ ...props }: MantineStyleProps) {
	const { settingsData } = useAEMAppSettings(
		AEM_APP_SETTINGS_COLLECTIONS.GLOBAL
	)
	const { isDismissed, dismissNotification } = useDismissedNotifications()
	const [notifications, setNotifications] = useState<AEMNotificationItem[]>([])
	const { toggleMenu, isMenuOpen } = useAppShellContext()
	const { colorScheme } = useMantineColorScheme()
	const navigate = useNavigate()
	const isMobile = useIsMobile()
	const isTablet = useIsTablet()
	const { userProfile, isAuthenticated, logout } = useAuthContext()
	const { notificationsData } = useAEMNotificationsData()
	const { dictionary } = useAEMDictionary()

	const [weatherIconPath, setWeatherIconPath] = useState<string>("")
	const [temperature, setTemperature] = useState<string>("")
	const { lat, long } = getLocation()

	const getWeatherData = async () => {
		if (lat && long) {
			let unit = ""
			if (lat && long) {
				unit = userProfile?.countryCode === "USA" ? "f" : "m"
			}
			const weather = await getWeather(`${lat},${long}`, unit)
			if (weather) {
				const code = weather.current?.weatherCode
				const temp = weather.current?.temperature
				setTemperature(`${temp}°${unit === "f" ? "F" : "C"}`)
				const icon = getWeatherIcon(
					code,
					colorScheme === "dark" ? "dark" : "light"
				)
				setWeatherIconPath(icon)
			}
		}
	}

	useEffect(() => {
		getWeatherData()
	}, [userProfile, lat, long])

	useEffect(() => {
		if (notificationsData) {
			const activeNotifications = notificationsData.filter(
				(notification: AEMNotificationItem) => !isDismissed(notification.id)
			)
			setNotifications(activeNotifications)
		}
	}, [notificationsData, userProfile?.dismissedNotifications])

	const handleNotificationDismiss = async (id: string) => {
		if (!notificationsData) {
			return
		}
		const selectedNotification = notificationsData.find(
			(notification: AEMNotificationItem) => notification.id === id
		)
		if (selectedNotification?.start && selectedNotification?.end) {
			await dismissNotification({
				notificationId: selectedNotification.id,
				start: new Date(selectedNotification.start),
				end: new Date(selectedNotification.end)
			})
		}
	}

	const logo = colorScheme === "dark" ? cPLogoDark : cPLogo

	const IconDivider = ({ visibleFrom = "base" }) => (
		<Divider
			visibleFrom={visibleFrom}
			orientation="vertical"
			h={18}
			className={styles.divider}
		/>
	)
	const { availableAffiliations } = useUserStore()
	const photo = userProfile?.agentPhotos?.AGENT_HOMEPAGE_PHOTO?.fullPath

	const activeAffiliation =
		availableAffiliations.find((a) => a.active) ??
		availableAffiliations.find((a) => a.isDefault) ??
		null
	const headerAgencyName =
		activeAffiliation?.agencyName ?? userProfile?.agencyName ?? ""

	const showLatteChat = isAuthenticated && userProfile?.latteAvailable

	const redirectTo = () => {
		const launchElement = document.getElementById("launch")
		launchElement ? launchElement?.click() : null

		eventTrackingItems({
			event_name: "chat_bot_engagement",
			link_url: "",
			chatbot_interaction_type: "Chat bot interaction type",
			event214: 1
		})
	}

	const handleLogOut = () => {
		eventTrackingItems({
			event_name: "click",
			link_url: "",
			category: "",
			interactionType: "",
			navLinkText: ""
		})
	}

	return (
		<AppShell.Header
			withBorder={false}
			{...props}
			h={{ base: 48, sm: 70 }}
			className={styles.appShell}
			pos={"fixed"}
		>
			<Group
				px={{ base: "sm", md: "xl" }}
				wrap="nowrap"
				align="center"
				justify="space-between"
				h="100%"
				w="100%"
			>
				<Group gap="md" wrap="nowrap">
					<Burger
						opened={isMenuOpen}
						color="var(--text-color-primary)"
						onClick={(e) => {
							e.stopPropagation()
							toggleMenu()
						}}
						aria-label="Toggle navigation"
						hiddenFrom="md"
						aria-expanded={isMenuOpen ? "true" : "false"}
					/>
					<UnstyledButton
						component="a"
						onClick={() => navigate("/home")}
						aria-label="Navigate to CruisingPower homepage"
					>
						<Image
							src={logo}
							alt="Company Logo"
							w={{ base: 199, md: 214, sm: 214 }}
							h={{ base: 28, md: 31, sm: 31 }}
						/>
					</UnstyledButton>
				</Group>
				<Flex
					gap={{ base: 6, md: "sm" }}
					justify="center"
					align="center"
					wrap="nowrap"
				>
					{/* Santiago Ponce: Business decided to remove the language picker for current version */}
					{VITE_USE_LANGUAGE_PICKER &&
						!isMobile &&
						settingsData?.showGlobalLanguageSelector && (
							<LanguagePicker
								showLabel={!isTablet}
								dataQaAttribute="header.button.language"
							/>
						)}
					<DrawerWithTrigger
						visibleFrom="sm"
						triggerIcon={<DashboardIcon data-qa="header.button.extensions" />}
						title={dictionary?.widgetCustomizeTitle}
						drawerContent={<YourExtensions />}
						openTriggerLabel="Open Your Extensions"
						closeTriggerLabel="Close Your Extensions modal"
						hashId="your-extensions"
					/>
					<IconDivider visibleFrom="sm" />
					{showLatteChat && (
						<>
							<UnstyledButton
								visibleFrom="sm"
								display="flex"
								onClick={redirectTo}
								variant={"header"}
								aria-label={"Open Latte Chat service"}
							>
								<ChatIcon data-qa="header.button.latte" />
							</UnstyledButton>
							<IconDivider visibleFrom="sm" />
						</>
					)}
					<DrawerWithTrigger
						title="Your Notifications"
						openTriggerLabel="Open Notifications Modal"
						closeTriggerLabel="Close Notifications Modal"
						triggerIcon={<NotificationBell numNotis={notifications.length} />}
						drawerContent={
							<Notifications
								notifications={notifications}
								onDismiss={handleNotificationDismiss}
							/>
						}
						hashId="your-notifications"
					/>
					{!isMobile && (
						<>
							<IconDivider visibleFrom="sm" />
							<Group gap={4} wrap="nowrap">
								{weatherIconPath !== "" ? (
									<AEMImage
										imageData={{
											title: "",
											description: "",
											width: 80,
											height: 80,
											path: weatherIconPath
										}}
										style={{
											objectFit: "contain",
											minWidth: "24px",
											minHeight: "24px"
										}}
									/>
								) : (
									<></>
								)}
								<Text
									span
									fz={12}
									fw={500}
									lh={1.75}
									variant="nowrap"
									className={styles.weatherText}
								>
									{temperature}
								</Text>
							</Group>
						</>
					)}
					<IconDivider />
					{SHOW_MULTI_AGENCY &&
						userProfile?.hasMultiAgencyAccess &&
						headerAgencyName &&
						isAuthenticated &&
						!isMobile && (
							<Text
								fz={12}
								fw={500}
								lh={1.75}
								visibleFrom="sm"
								variant="nowrap"
								className={styles.weatherText}
							>
								{headerAgencyName}
							</Text>
						)}
					<DrawerWithTrigger
						withHeader={false}
						triggerIcon={
							<Avatar
								variant="transparent"
								size={32}
								src={photo}
								color="var(--text-color-primary)"
								data-qa="header.button.profile"
							/>
						}
						drawerContent={(close) => <AccountMenu onDrawerClose={close} />}
						openTriggerLabel="Open Your Profile modal"
						closeTriggerLabel="Close profile modal"
					/>
					{VITE_USE_DARK_MODE && (
						<>
							<IconDivider visibleFrom="sm" />
							<ThemeSwitch visibleFrom="sm" />
						</>
					)}
					{USE_GLOBAL_SEARCH && (
						<>
							<IconDivider visibleFrom="sm" />
							<UnstyledButton
								component="a"
								onClick={() => navigate("/global-search")}
								aria-label="Navigate to global search page"
							>
								<SearchIcon color="var(--text-color-primary)" />
							</UnstyledButton>
						</>
					)}
					{!isMobile && (
						<>
							<IconDivider visibleFrom="sm" />
							<UnstyledButton
								onClick={() => {
									logout()
									handleLogOut()
								}}
								variant="icon-hover"
								className="logoutBtn"
							>
								<Group visibleFrom="sm" gap="xs" data-qa="header.button.logout">
									<LogoutIcon />
									<Text span fz={9} fw={500} lh={2.2}>
										{dictionary?.LogoutLabel || ""}
									</Text>
								</Group>
							</UnstyledButton>
						</>
					)}
				</Flex>
			</Group>
		</AppShell.Header>
	)
}

export default AppHeader
